import { useConvertCurrencyForConsumerQuery } from '../generated/graphql'
import { useTranslation } from '../localization/useTranslation'
import { useAuthStore } from '../store/auth'
import { currencySuffix, money } from './money'

/**
 * The unit shown after a reward amount, locale-aware. Some shops pay in points rather than
 * money — Core reports that as `currency: "POINT"` — so it renders the localized "points" word;
 * everything else falls back to the currency symbol (`currencySuffix`: DKK→kr, EUR→€).
 */
export function useCurrencySuffix(): (currency?: string | null) => string {
  const { t } = useTranslation()
  return (currency) => (currency === 'POINT' ? t('common.points') : currencySuffix(currency))
}

// Convert a large base amount to get an accurate rate with minimal rounding error, then apply it
// locally to every reward figure — one conversion call instead of one per amount.
const RATE_BASE = 100_000

interface ConsumerRate {
  /** Multiply a shop-currency amount by this to get the consumer-currency amount. */
  rate: number
  /** The currency amounts are shown in (the consumer's), for the suffix. */
  currency?: string | null
  /** False only while the conversion is still loading (when one is needed). */
  ready: boolean
}

/**
 * Reward/offer values from Core are in the shop's base currency (`shopCurrency`), but the consumer
 * has their own display currency (`currency`, from ConsumerLogin). When they differ we must convert
 * — otherwise a 9 EUR offer would read "9 kr" while the (currency-aware) search shows "66 kr". This
 * mirrors v1's Currency component: convert shop→consumer via `ConvertCurrencyForConsumer`, or skip
 * when the currencies already match. A single call derives a rate applied to every figure.
 */
export function useConsumerRate(): ConsumerRate {
  const consumerId = useAuthStore((s) => s.consumerId)
  const shopCurrency = useAuthStore((s) => s.config.shopCurrency)
  const consumerCurrency = useAuthStore((s) => s.config.currency)
  const needsConversion =
    !!consumerId && !!shopCurrency && !!consumerCurrency && shopCurrency !== consumerCurrency

  const { data } = useConvertCurrencyForConsumerQuery({
    variables: { inputCurrency: shopCurrency ?? 'DKK', inputValue: RATE_BASE, consumerId: consumerId ?? '' },
    skip: !needsConversion,
    fetchPolicy: 'cache-first',
  })

  if (!needsConversion) {
    return { rate: 1, currency: consumerCurrency ?? shopCurrency, ready: true }
  }
  const converted = data?.ConvertCurrencyForConsumer
  if (!converted?.value || converted.currency !== consumerCurrency) {
    return { rate: 1, currency: consumerCurrency, ready: false }
  }
  return { rate: converted.value / RATE_BASE, currency: converted.currency ?? consumerCurrency, ready: true }
}

/**
 * Returns a formatter for reward amounts: give it a shop-currency value, get back the display
 * string in the consumer's currency, e.g. `fmt(9)` → "66 kr" (or "9 €" when no conversion applies).
 * While a needed conversion is still loading it renders a thin placeholder rather than a wrong
 * (unconverted) figure.
 */
export function useMoneyFmt(): (shopAmount?: number | null) => string {
  const { rate, currency, ready } = useConsumerRate()
  const suffix = useCurrencySuffix()(currency)
  return (shopAmount) => {
    if (!ready) return '…'
    return `${money(Math.round((shopAmount ?? 0) * rate))} ${suffix}`
  }
}
